import { getTenantBrandingConfig, updateTenantBranding } from '@t4s/api-client'
import type { StoredWhiteLabelConfig, UpdateBrandingRequest } from '@t4s/api-client/model'
import { unwrap } from '../lib/api-error.ts'

/**
 * The generated calls the branding screen is made of —
 * docs/ux/PLATFORM-ADMIN.md §13.1, §13.5.
 *
 * **This task adds no route.** `GET /platform/tenants/{tenantId}/branding-config` is
 * W01b's, exactly as documented: a retired tenant's real branding, never the neutral
 * default the public route substitutes, with the tenant's `buildKey` copied verbatim from
 * its row (since `build-key-contract` the key is a stored, rotatable value, not something
 * minted per call — reading the document twice answers the same key until a rotation
 * changes it). The tenant Overview reads it for the Build key card; the branding screen
 * reads it once per visit rather than polling. `PATCH /platform/tenants/{tenantId}/branding`
 * is P04a-api's: it merges `brandColor`/`logoUrl` into the stored document and answers
 * with the same regenerated shape the read above returns, so a save's response can
 * replace the cached read directly (P04b) with no second network call.
 *
 * Both answer `StoredWhiteLabelConfig`, not the public file's `WhiteLabelConfig`
 * (`fix-branding-overlong-stored-colour-500`): `colors.brand` and `logoUrl` are exactly what is
 * stored, so a stored colour over 64 characters reaches this screen in full and §13.4's
 * stored-length notice can name it. That state used to answer `500` here.
 */
export function fetchTenantBranding(tenantId: string): Promise<StoredWhiteLabelConfig> {
  return unwrap<StoredWhiteLabelConfig>(getTenantBrandingConfig(tenantId))
}

export function saveTenantBranding(
  tenantId: string,
  patch: UpdateBrandingRequest,
): Promise<StoredWhiteLabelConfig> {
  return unwrap<StoredWhiteLabelConfig>(updateTenantBranding(tenantId, patch))
}

export const brandingQueryKey = (tenantId: string) =>
  ['platform', 'tenant', tenantId, 'branding'] as const
